// The inspector's AI tab, drawn like Flowmark's: say what should change in
// plain words (or pick an idea), and the AI rewrites the words of the step on
// the canvas. Only words change; links, images and settings stay as they are
// (see model/page-ai). The editor saves a version first, so Undo or History
// brings the page back. The request goes to POST /api/pages/ai-edit.
import { useId, useRef, useState } from 'react';
import type { Section } from '../model/pages';
import { applyAiChanges, projectForAi } from '../model/page-ai';
import { CircleAlertIcon, SparklesIcon } from '../icons';
import { useEditorApi } from './editorApi';
import { HINT, LABEL, TEXTAREA } from './InspectorKit';

export const AI_IDEAS = [
  'Make the headline shorter and clearer',
  'Rewrite this page in a warmer, friendlier tone',
  'Add three common questions to the FAQ',
  'Tighten every paragraph to two sentences',
] as const;

export function AiPanel({ sections, pageName, scopeLabel, disabled, onApply }: {
  /** The sections on the canvas (the step, or the thank-you page). */
  sections: Section[];
  pageName: string;
  /** What the button applies to: "this page", "this step", "the thank-you page". */
  scopeLabel: string;
  disabled?: boolean;
  /** The AI's answer and its one-line summary. The editor applies it (with
   *  applyAiChanges) to the sections as they are by then, so edits made while
   *  the AI worked are kept. Only called when the answer changes something. */
  onApply: (result: unknown, summary: string) => void;
}) {
  const api = useEditorApi();
  const [prompt, setPrompt] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxId = useId();
  const ids = useRef(0);

  async function run() {
    const instruction = prompt.trim();
    if (!instruction || running || disabled) return;
    setRunning(true);
    setError(null);
    try {
      const answer = await api.aiEditSections({ instruction, page_name: pageName, sections: projectForAi(sections) });
      const result: unknown = answer?.result;
      const summary = typeof answer?.summary === 'string' ? answer.summary : '';
      const applied = applyAiChanges(sections, result, () => `s_ai_${Date.now().toString(36)}_${ids.current++}`);
      if (applied.changed + applied.added + applied.removed === 0) {
        setError(summary ? `Nothing changed. ${summary}` : 'Nothing changed. Try saying it another way.');
        return;
      }
      onApply(result, summary);
      setPrompt('');
    } catch (e) {
      setError((e as Error).message || 'The AI could not make that change. Try again.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="min-w-0 pb-8" data-ai-panel>
      <div className="flex min-w-0 items-center gap-2.5 px-4 pb-3 pt-3.5">
        <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-surface2 text-text/80">
          <SparklesIcon size={15} />
        </span>
        <h2 className="truncate text-base font-semibold tracking-[-0.01em] text-text">Edit with AI</h2>
      </div>
      <section className="grid min-w-0 gap-3.5 border-t border-border px-4 pb-4 pt-3">
        <div className="grid min-w-0 gap-1.5">
          <label htmlFor={boxId} className={LABEL}>What should change?</label>
          <textarea id={boxId} rows={5} className={`${TEXTAREA} min-h-[116px]`} value={prompt} disabled={running}
            placeholder="Describe the change in plain words…"
            onChange={(e) => { setPrompt(e.target.value); if (error) setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void run(); } }} />
        </div>
        <div role="group" aria-label="Ideas" className="flex min-w-0 flex-wrap gap-1.5">
          {AI_IDEAS.map((idea) => (
            <button key={idea} type="button" disabled={running}
              className="rounded-full border border-border px-2.5 py-[5px] text-left text-xs text-text/80 transition-colors duration-100 hover:border-borderHi hover:bg-text/[0.04] hover:text-text disabled:pointer-events-none disabled:opacity-40"
              onClick={() => { setPrompt(idea); setError(null); }}>
              {idea}
            </button>
          ))}
        </div>
        {error && (
          <p role="alert" className="flex min-w-0 items-start gap-2 rounded-lg border border-danger/20 bg-danger/[0.06] px-2.5 py-2 text-xs leading-[1.45] text-danger">
            <CircleAlertIcon size={14} className="mt-px" />
            <span className="min-w-0">{error}</span>
          </p>
        )}
        <button type="button" className="btn-primary h-8 w-full justify-center gap-1.5" disabled={!prompt.trim() || running || disabled}
          aria-busy={running || undefined} onClick={() => { void run(); }}>
          {running ? <><span className="pe-spinner" aria-hidden="true" />Working on it…</> : <><SparklesIcon size={14} />Apply to {scopeLabel}</>}
        </button>
        <p className={HINT}>Only the words change. You can undo it, or restore an earlier version from History.</p>
      </section>
    </div>
  );
}
